import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { groups as groupsApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Users, Loader2, DollarSign, ExternalLink, Trash2, UserPlus, X, Banknote, RefreshCcw } from 'lucide-react';

// Organizer dashboard — /groups/mine. Shows every group I organize with
// live commission stats, a Stripe Connect onboarding CTA at the top, and
// per-group drill-down (members, invite, commissions, payouts, edit).
export default function OrganizerGroups() {
  const [rows, setRows] = useState(null);
  const [connect, setConnect] = useState(null);
  const [openGid, setOpenGid] = useState(null);
  const [detail, setDetail] = useState(null); // { members, commissions, payouts }
  const [inviteEmail, setInviteEmail] = useState('');

  const load = async () => {
    try {
      const [gs, cs] = await Promise.all([
        groupsApi.myOrganized(),
        groupsApi.connectStatus().catch(() => ({ connected: false })),
      ]);
      setRows(gs || []); setConnect(cs);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const openGroup = async (g) => {
    setOpenGid(g.id); setDetail({ loading: true });
    try {
      const [members, commissions, payouts] = await Promise.all([
        groupsApi.members(g.id),
        groupsApi.myCommissions(g.id),
        groupsApi.myPayouts(g.id),
      ]);
      setDetail({ members, commissions, payouts });
    } catch (e) { toast({ title: 'Load failed' }); setDetail(null); }
  };
  const startOnboarding = async () => {
    try {
      const r = await groupsApi.connectOnboarding(window.location.href);
      window.location.href = r.url;
    } catch (e) { toast({ title: 'Stripe onboarding failed', description: e?.response?.data?.detail || '' }); }
  };
  const refreshConnect = async () => {
    try { setConnect(await groupsApi.connectStatus()); toast({ title: 'Stripe status refreshed' }); }
    catch { /* ignore */ }
  };
  const invite = async (gid) => {
    if (!inviteEmail.trim()) return;
    try {
      await groupsApi.invite(gid, inviteEmail.trim());
      setInviteEmail('');
      toast({ title: 'Member added' });
      openGroup({ id: gid });
    } catch (e) { toast({ title: 'Invite failed', description: e?.response?.data?.detail || '' }); }
  };
  const removeMember = async (gid, uid) => {
    if (!window.confirm('Remove this member from the group?')) return;
    try {
      await groupsApi.removeMember(gid, uid);
      toast({ title: 'Member removed' });
      openGroup({ id: gid });
    } catch { toast({ title: 'Remove failed' }); }
  };

  if (rows === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="organizer-groups-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users size={22}/> My groups (organizer)</h1>
          <p className="text-sm text-gray-400 mt-1">10% commission on member tier subscriptions + membership fees. Payouts via Stripe Connect.</p>
        </div>
        <Link to="/groups" data-testid="organizer-browse-link"
              className="text-sm px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">Browse all groups</Link>
      </div>

      {/* Stripe Connect banner */}
      <StripeConnectBanner status={connect} onStart={startOnboarding} onRefresh={refreshConnect}/>

      {/* Groups list */}
      {rows.length === 0 ? (
        <div className="text-gray-500 text-sm border border-dashed border-gray-800 rounded-lg p-6 text-center">
          You haven't created a group yet. Head to <Link to="/groups" className="underline">Groups</Link> to spin one up.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(g => (
            <div key={g.id} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden"
                 data-testid={`organizer-group-${g.id}`}>
              <button onClick={() => openGid === g.id ? setOpenGid(null) : openGroup(g)}
                      className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-800/50">
                {g.coverImage ? <img src={g.coverImage} alt="" className="w-12 h-12 rounded object-cover"/> : <div className="w-12 h-12 rounded bg-gray-800"/>}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{g.name}</div>
                  <div className="text-xs text-gray-500">{g.memberCount || 0} member{g.memberCount === 1 ? '' : 's'} · <Link to={`/groups/${g.slug}`} className="underline">public page</Link></div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-green-400 font-semibold">${(g.stats?.pendingCommissionUsd || 0).toFixed(2)}</div>
                  <div className="text-gray-500">pending payout</div>
                </div>
                <div className="text-right text-xs ml-4">
                  <div className="text-white font-semibold">${(g.stats?.paidCommissionUsd || 0).toFixed(2)}</div>
                  <div className="text-gray-500">paid</div>
                </div>
              </button>
              {openGid === g.id && (
                <div className="border-t border-gray-800 p-4 space-y-4" data-testid={`organizer-group-detail-${g.id}`}>
                  {!detail || detail.loading ? (
                    <div className="text-gray-500 text-sm inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading…</div>
                  ) : (
                    <>
                      {/* Members */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white text-sm font-medium">Members ({detail.members.length})</div>
                          <div className="flex gap-2">
                            <input placeholder="member@email.com" value={inviteEmail}
                                   data-testid={`organizer-invite-input-${g.id}`}
                                   onChange={e => setInviteEmail(e.target.value)}
                                   className="bg-black border border-gray-700 rounded px-2 py-1 text-white text-xs"/>
                            <button data-testid={`organizer-invite-btn-${g.id}`} onClick={() => invite(g.id)}
                                    className="text-xs pbb-btn-green inline-flex items-center gap-1">
                              <UserPlus size={11}/> Add member
                            </button>
                          </div>
                        </div>
                        <div className="max-h-56 overflow-y-auto space-y-1">
                          {detail.members.length === 0 && <div className="text-gray-500 text-xs">No members yet.</div>}
                          {detail.members.map(m => (
                            <div key={m.id} className="flex items-center gap-2 text-sm border border-gray-800 rounded p-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-white truncate">{m.userName || '—'}</div>
                                <div className="text-xs text-gray-500 truncate">{m.userEmail} · <span className={m.status === 'active' ? 'text-green-400' : 'text-gray-500'}>{m.status}</span></div>
                              </div>
                              {m.status === 'active' && (
                                <button data-testid={`organizer-remove-${g.id}-${m.userId}`}
                                        onClick={() => removeMember(g.id, m.userId)}
                                        className="text-red-400 hover:text-red-300"><X size={14}/></button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Commissions */}
                      <div>
                        <div className="text-white text-sm font-medium mb-2">Commissions ({detail.commissions.length})</div>
                        <div className="max-h-56 overflow-y-auto text-xs">
                          <table className="w-full">
                            <thead className="text-gray-500 uppercase text-[10px]">
                              <tr><th className="text-left py-1">When</th><th className="text-left py-1">Source</th><th className="text-right py-1">Base</th><th className="text-right py-1">Commission</th><th className="text-left py-1 pl-2">Status</th></tr>
                            </thead>
                            <tbody className="text-gray-300">
                              {detail.commissions.map(c => (
                                <tr key={c.id} className="border-t border-gray-800">
                                  <td className="py-1">{new Date(c.createdAt).toLocaleDateString()}</td>
                                  <td className="py-1">{c.sourceType === 'membership_fee' ? 'Membership fee' : 'Tier sub'}</td>
                                  <td className="py-1 text-right">${Number(c.amountUsd).toFixed(2)}</td>
                                  <td className="py-1 text-right text-green-400">${Number(c.commissionUsd).toFixed(2)}</td>
                                  <td className="py-1 pl-2">{c.status.replace(/_/g,' ')}</td>
                                </tr>
                              ))}
                              {!detail.commissions.length && <tr><td colSpan={5} className="text-gray-500 text-center py-2">No commissions yet.</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {/* Payouts */}
                      <div>
                        <div className="text-white text-sm font-medium mb-2 flex items-center gap-1"><Banknote size={14}/> Payouts ({detail.payouts.length})</div>
                        {detail.payouts.length === 0 ? (
                          <div className="text-gray-500 text-xs">No payouts sent yet. Admin batches these to your Stripe Connect account.</div>
                        ) : (
                          <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                            {detail.payouts.map(p => (
                              <div key={p.id} className="flex items-center justify-between border border-gray-800 rounded p-2">
                                <div className="text-gray-300">{new Date(p.createdAt).toLocaleString()}</div>
                                <div className="text-green-400 font-semibold">${Number(p.amountUsd).toFixed(2)}</div>
                                <div className="text-gray-500 truncate max-w-[160px]">{p.stripeTransferId}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StripeConnectBanner({ status, onStart, onRefresh }) {
  if (!status) return null;
  const ready = status.connected && status.payoutsEnabled;
  return (
    <div className={`border rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap ${ready ? 'bg-green-500/5 border-green-500/40' : 'bg-yellow-500/5 border-yellow-500/40'}`}
         data-testid="connect-banner">
      <div className="min-w-0">
        <div className="text-white text-sm font-semibold">
          {ready ? 'Stripe Connect ready — payouts enabled' : 'Complete Stripe onboarding to receive payouts'}
        </div>
        <div className="text-xs text-gray-400 mt-1">
          {ready
            ? 'Your bank account is connected. Membership fees route to you automatically; commission payouts run in batches.'
            : 'Group commissions accrue right away, but nothing pays out until Stripe verifies your account.'}
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid="connect-refresh" onClick={onRefresh}
                className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
          <RefreshCcw size={12}/> Refresh
        </button>
        {!ready && (
          <button data-testid="connect-onboarding-start" onClick={onStart}
                  className="text-xs pbb-btn-green inline-flex items-center gap-1">
            <ExternalLink size={12}/> {status.connected ? 'Continue onboarding' : 'Start Stripe onboarding'}
          </button>
        )}
      </div>
    </div>
  );
}
